// deps
import React, { memo, ReactElement, useMemo } from "react";

// components
import { AmountBox } from "../amount-selection-box";
import { HorizontalScroller } from "@core-ui/components/horizontal-scroller";

// interfaces
import {
    OnAmountBoxSelectionParams,
    AmountBoxData,
    AmountOption,
    AmountBoxTextIds,
} from "@core-ui/interfaces/AmountWidget";

// helpers
import { isEmpty } from "@core-ui/helpers/Utils";

import styles from "./AmountRadioWrapper.v4.scss";
import { AmountSuggestionWrapper } from "./styles";

const AMOUNT_ITEM_VISIBILITY_THRESHOLD = 1;

export interface AmountSelectionWrapperProps {
    amountOptions: AmountOption[];
    selectedAmount: number | undefined;
    onPredefinedAmountBoxSelection: (args: OnAmountBoxSelectionParams) => void;
    suggestionBoxDataTestIds: AmountBoxTextIds;
}

const getItems = (amountBoxesData: AmountBoxData[]): ReactElement[] => {
    return amountBoxesData.map(amountBoxData => <AmountBox key={amountBoxData.text} {...amountBoxData} />);
};

const getAmountBoxesData = (
    selectedAmount: number | undefined,
    options: AmountOption[],
    dataTestIds: AmountBoxTextIds,
    onPredefinedAmountBoxSelection: (args: OnAmountBoxSelectionParams) => void,
): AmountBoxData[] => {
    const amountBoxesData = options.map(amountOption => {
        const { amount, mostPopular } = amountOption;
        const isPredefinedSelection = amount === selectedAmount;
        return {
            selected: isPredefinedSelection,
            boxAmount: amount,
            text: amount.toString(),
            showMostPopularText: !!mostPopular,
            showRsIcon: true,
            onAmountBoxSelection: onPredefinedAmountBoxSelection,
            dataTestIds,
        };
    });

    return amountBoxesData;
};

const AmountRadioWrapper = ({
    amountOptions,
    selectedAmount,
    onPredefinedAmountBoxSelection,
    suggestionBoxDataTestIds,
}: AmountSelectionWrapperProps): ReactElement | null => {
    const items = useMemo(() => {
        const amountBoxData = getAmountBoxesData(
            selectedAmount,
            amountOptions,
            suggestionBoxDataTestIds,
            onPredefinedAmountBoxSelection,
        );
        return getItems(amountBoxData);
    }, [amountOptions, onPredefinedAmountBoxSelection, selectedAmount, suggestionBoxDataTestIds]);

    if (isEmpty(amountOptions)) return null;

    return (
        <AmountSuggestionWrapper>
            <HorizontalScroller
                containerClassName={styles.amountWrapperScrollBarContainer}
                itemClassName={styles.amountWrapperItemContainer}
                items={items}
                itemVisibilityThreshold={AMOUNT_ITEM_VISIBILITY_THRESHOLD}
            />
        </AmountSuggestionWrapper>
    );
};

export default memo(AmountRadioWrapper);
